import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteIcon from "@material-ui/icons/Delete";
import PaymentDetailRegistryForm from "./PaymentDetailRegistryForm";
import {
  actualizarOrdenDeRegistro,
  marcarAtletaRemovidoDelGrupo,
} from "../../../../../data-store/actions/registry-actions";

// Pantalla que se muestra antes de pagar cuando ya hay más de un atleta en
// el grupo (después de usar "Agregar otro participante" al menos una vez).
// El primer atleta guardado (grupoAtletas[0]) es el "líder": es al único al
// que se le va a cobrar de verdad. Al confirmar, se consolida su orden (sus
// propios ítems + un resumen por cada atleta adicional + la comisión sobre
// el total) y se le agrega a cada atleta adicional un ítem que deja su
// propia orden en $0, referenciando el pago real del líder — las pasarelas
// de pago no se tocan, solo reciben la orden ya consolidada del líder.
const useStyles = makeStyles((theme) => ({
  root: { width: "100%" },
  paper: { padding: "15px", marginBottom: "10px" },
}));

function sumarOrden(order) {
  return (order || []).reduce(
    (acc, item) => acc + (parseFloat(item.unit_price) || 0) * (item.quantity || 0),
    0
  );
}

export default function GroupRegistrySummary(props) {
  const {
    grupoAtletas,
    eventoIndex,
    currency,
    currencyBase,
    registryConfigurationData,
    onQuitarAtleta,
    onCancelarGrupo,
    onListo,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation("inscripcion");
  const [guardando, setGuardando] = useState(false);

  if (!grupoAtletas || grupoAtletas.length === 0) return null;

  const lider = grupoAtletas[0];
  const adicionales = grupoAtletas.slice(1);
  const comisionGlobal =
    registryConfigurationData.configuracion.formasDePago.comisionGlobal;

  const subtotalConsolidado = grupoAtletas.reduce(
    (acc, atleta) => acc + sumarOrden(atleta.atleteData.order),
    0
  );
  const comision =
    Math.round(subtotalConsolidado * comisionGlobal * 100) / 100;

  const ordenLiderConsolidada = [
    ...lider.atleteData.order,
    ...adicionales.map((atleta) => ({
      code: "atleta-vinculado",
      name: t("grupo.inscripcionDe", {
        nombres: atleta.resumen.nombres,
        apellidos: atleta.resumen.apellidos,
      }),
      description: t("grupo.distanciaRamaCategoria", {
        distancia: atleta.resumen.distancia,
        rama: atleta.resumen.rama,
        categoria: atleta.resumen.categoria,
      }),
      precioKey: "",
      quantity: 1,
      unit_price: sumarOrden(atleta.atleteData.order),
      inscritoKeyVinculado: atleta.inscritoKey,
    })),
    {
      code: "comision",
      name: t("comisionNombre"),
      description: t("comisionDescripcion"),
      precioKey: "",
      quantity: 1,
      unit_price: comision,
    },
  ];

  async function confirmarYPagar() {
    setGuardando(true);
    try {
      await actualizarOrdenDeRegistro(eventoIndex, lider.inscritoKey, ordenLiderConsolidada);
      await Promise.all(
        adicionales.map((atleta) => {
          const subtotalAtleta = sumarOrden(atleta.atleteData.order);
          const ordenConCero = [
            ...atleta.atleteData.order,
            {
              code: "pago-en-grupo",
              name: t("grupo.pagoIncluidoEnInscripcionDe", {
                nombres: lider.resumen.nombres,
                apellidos: lider.resumen.apellidos,
              }),
              description: t("grupo.folioDeRegistro", {
                inscritoKey: lider.inscritoKey,
              }),
              precioKey: "",
              quantity: 1,
              unit_price: -subtotalAtleta,
            },
          ];
          return actualizarOrdenDeRegistro(eventoIndex, atleta.inscritoKey, ordenConCero);
        })
      );
      onListo(ordenLiderConsolidada, lider);
    } finally {
      setGuardando(false);
    }
  }

  function quitar(atleta) {
    marcarAtletaRemovidoDelGrupo(eventoIndex, atleta.inscritoKey);
    onQuitarAtleta(atleta.inscritoKey);
  }

  return (
    <Grid container direction="row" className={classes.root}>
      <Grid item xs={12} style={{ padding: "15px", textAlign: "center" }}>
        <Typography color="primary" gutterBottom variant={"h4"} component="h2">
          {t("grupo.resumenDelGrupo")}
        </Typography>
        <Typography color="primary" gutterBottom variant={"subtitle2"} component="h5">
          {t("grupo.pagoUnaSolaVez", {
            nombres: lider.resumen.nombres,
            apellidos: lider.resumen.apellidos,
          })}
        </Typography>
      </Grid>

      <Grid item xs={12} style={{ padding: "0 15px" }}>
        {grupoAtletas.map((atleta, idx) => (
          <Paper key={atleta.inscritoKey} className={classes.paper}>
            <Grid container alignItems="center">
              <Grid item xs>
                <Typography variant="subtitle1">
                  {atleta.resumen.nombres} {atleta.resumen.apellidos}{" "}
                  {idx === 0 && <b>{t("grupo.pagaInscripcionDeTodos")}</b>}
                </Typography>
                <Typography variant="body2">
                  {atleta.resumen.distancia} · {atleta.resumen.rama} · {atleta.resumen.categoria}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2">
                  {sumarOrden(atleta.atleteData.order).toFixed(2)} {currency.codigo}
                </Typography>
              </Grid>
              {idx > 0 && (
                <Grid item>
                  <IconButton onClick={() => quitar(atleta)} disabled={guardando}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </Paper>
        ))}
      </Grid>

      <Grid item xs={12} style={{ padding: "15px" }}>
        <PaymentDetailRegistryForm
          order={ordenLiderConsolidada}
          currency={currency}
          currencyBase={currencyBase}
          removeItemOrder={() => false}
        />
      </Grid>

      <Grid item xs={12} style={{ padding: "15px", textAlign: "center" }}>
        <Button
          onClick={confirmarYPagar}
          variant="contained"
          color="primary"
          disabled={guardando}
          style={{ height: "80px", width: "100%", fontSize: "18px" }}
        >
          {guardando ? <CircularProgress size={24} /> : t("grupo.pagarTodo")}
        </Button>
        <Button
          onClick={onCancelarGrupo}
          disabled={guardando}
          style={{ marginTop: "10px" }}
        >
          {t("grupo.cancelarGrupo")}
        </Button>
      </Grid>
    </Grid>
  );
}
